import { FloatingPanel } from './FloatingPanel';
import { formatCheckNumber, formatCurrency, formatDateRange, formatShortDate, formatText } from '@/utils/format';
import type { ClaimRow } from '../types/claims.types';

interface ClaimDetailPanelProps {
  readonly claim: ClaimRow;
  onClose: () => void;
}

/**
 * Claim detail (US-012). It floats over the page rather than sharing the row with the grid, so the grid
 * keeps its full width behind it and the sort, filter and pagination are untouched when it closes
 * (AC-003).
 */
export function ClaimDetailPanel({ claim, onClose }: ClaimDetailPanelProps): React.JSX.Element {
  return (
    <FloatingPanel
      title="Details"
      role="dialog"
      closeLabel="Close claim details"
      onClose={onClose}
    >
      <dl className="grid gap-2 text-[13px]">
        <Row label="Claim Number" value={formatText(claim.claimNumber)} />
        <Row label="NPI" value={formatText(claim.npi)} />
        {/* Of these, only Coinsurance is still mock-only: the deployed API was called on 2026-09-29 and
            returns icnNumber, location and serviceCode populated on every entry. */}
        {/* Proc Code is deliberately absent from this whole-claim panel: it is claim-line data
            (QA 2026-09-09, Biren). It still renders in the grid via ClaimCells — per line when
            expanded, and on the header row when every line agrees on one code. */}
        <Row label="ICN" value={formatText(claim.icn)} />
        {/* PP-023 names these two for the grid; they label the same fields here, so they follow it. */}
        <Row label="Provider Name" value={formatText(claim.location)} />
        <Row label="Svc Code" value={formatText(claim.svcCode)} />
        <Row
          label="Date of Service"
          value={formatDateRange(claim.serviceDateFrom, claim.serviceDateTo)}
        />
        <Row label="Status" value={formatText(claim.status)} />
        <Row label="Billed Amount" value={formatCurrency(claim.billedAmount)} />
        <Row label="Medicare Allowed Amt" value={formatCurrency(claim.medicareAllowedAmount)} />
        <Row label="Medicare Paid Amt" value={formatCurrency(claim.paidAmount)} />
        <Row label="Carrier Paid Amt" value={formatCurrency(claim.carrierPaidAmount)} />
        <Row label="Coinsurance" value={formatCurrency(claim.coinsurance)} />
        <Row label="Payment Date" value={formatShortDate(claim.paymentDate)} />
        <Row label="Check Number" value={formatCheckNumber(claim.checkNumber)} />
        <Row label="Reason" value={formatText(claim.reason)} />
      </dl>
    </FloatingPanel>
  );
}

function Row({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="flex gap-3">
      <dt className="text-muted w-36 flex-none font-semibold">{label}</dt>
      <dd className="text-ink min-w-0 break-words">{value}</dd>
    </div>
  );
}
